"use client";

import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import ChipToken, { getChipColor } from "../../components/ChipToken";
import { maskPhone } from "../../helper";

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const emptyForm = () => ({ date: nowStr(), remarks: "" });

export default function ChipExchangeForm({ open, onClose, refresh }) {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [stockChips, setStockChips] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [onTableChips, setOnTableChips] = useState({});

  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundUser, setFoundUser] = useState(null);

  const [given, setGiven]       = useState({});
  const [received, setReceived] = useState({});

  const [form, setForm]           = useState({ ...emptyForm() });
  const [loading, setLoading]     = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitWarnings, setSubmitWarnings] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, date: nowStr() }));
    setStockLoading(true);
    Promise.all([
      axiosRequest("GET", `${apiBaseUrl}/chips`, token),
      axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token),
    ]).then(([chipsRes, summaryRes]) => {
        const allChips = Array.isArray(chipsRes?.data) ? chipsRes.data : (chipsRes?.data?.data ?? []);
        const chips = allChips.filter((c) => !c.chip_type || c.chip_type === "REGULAR");
        setStockChips(chips);
        const init = {};
        chips.forEach((c) => { init[c.denomination] = ""; });
        setGiven({ ...init });
        setReceived({ ...init });
        setOnTableChips(summaryRes?.data?.on_table_chips || {});
      })
      .catch(() => setStockChips([]))
      .finally(() => setStockLoading(false));
  }, [open]);

  const handleSearch = async () => {
    const input = phone.trim();
    if (!input) return;
    setSearching(true);
    setSearchError("");
    setFoundUser(null);
    try {
      const param = /^\d{10}$/.test(input) ? `phone=${input}` : `playerId=${input}`;
      const res = await axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token);
      const user = res?.data?.userDetails;
      if (user) setFoundUser(user);
      else setSearchError("No user found.");
    } catch {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

  const totalGiven    = stockChips.reduce((s, c) => s + calcAmount(c.denomination, given[c.denomination]),    0);
  const totalReceived = stockChips.reduce((s, c) => s + calcAmount(c.denomination, received[c.denomination]), 0);

  const isBalanced  = totalGiven > 0 && totalGiven === totalReceived;
  const difference  = totalReceived - totalGiven;

  const handleSave = async () => {
    setSubmitError("");
    setSubmitWarnings([]);
    try {
      setLoading(true);
      const chips_given = stockChips
        .filter((c) => Number(given[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(given[c.denomination]) }));

      const chips_received = stockChips
        .filter((c) => Number(received[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(received[c.denomination]) }));

      const res = await axiosRequest("POST", `${apiBaseUrl}/chip-exchange`, token, {
        player_id: foundUser?.playerId || "",
        user_name: foundUser?.name || "",
        phone:     foundUser?.phone || phone,
        ...form,
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        chips_given,
        chips_received,
      });

      if (res?.status === false || res?.data?.status === false) {
        setSubmitError(res?.message || res?.data?.message || "Exchange failed.");
        return;
      }

      const warnings = res?.data?.warnings || [];
      refresh();
      if (warnings.length > 0) {
        setSubmitWarnings(warnings);
      } else {
        handleClose();
      }
    } catch (err) {
      setSubmitError("Something went wrong.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setFoundUser(null);
    setSearchError("");
    setSubmitError("");
    setSubmitWarnings([]);
    setGiven({});
    setReceived({});
    setForm({ ...emptyForm() });
    onClose();
  };

  const overStock =
    stockChips.some((c) => Number(received[c.denomination] || 0) > c.quantity) ||
    stockChips.some((c) => Number(given[c.denomination] || 0) > Number(onTableChips[c.denomination] || 0));

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 800, p: 3, overflowY: "auto", height: "100%" }}>

        {/* HEADER */}
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <SwapHorizIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight={700}>Chip Exchange</Typography>
        </Box>
        <Typography color="text.secondary" variant="body2" mb={2}>
          Swap chip denominations — total given must equal total received
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* PHONE SEARCH */}
        <Typography fontWeight={600} mb={1}>Search Player (optional)</Typography>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            fullWidth size="small" label="Phone / Player ID"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            disabled={searching || !phone.trim()}
            sx={{ minWidth: 90, whiteSpace: "nowrap" }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {searchError && <Alert severity="error" sx={{ mb: 1 }}>{searchError}</Alert>}

        {foundUser && (
          <Paper
            elevation={2}
            sx={{
              p: 1.5, mb: 3, borderRadius: 2,
              background: "linear-gradient(135deg,#1e3c72,#2a5298)",
              color: "#fff",
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player</Typography>
                <Typography fontWeight={700}>{foundUser.name || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player ID</Typography>
                <Typography fontWeight={600}>{foundUser.playerId || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Phone</Typography>
                <Typography fontWeight={600}>{maskPhone(foundUser.phone) || "—"}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* CHIP TABLES */}
        {stockLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Grid container spacing={2} mb={3}>
            {/* CHIPS GIVEN (player brings in) */}
            <Grid item xs={6}>
              <Typography fontWeight={700} mb={1} color="error.main">Chips Given (Player Returns)</Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#fff3f3" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Denom ()</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">On Table / Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockChips.map((chip) => {
                      const qty     = given[chip.denomination] || "";
                      const amount  = calcAmount(chip.denomination, qty);
                      const onTable = Number(onTableChips[chip.denomination] || 0);
                      const isOver  = Number(qty) > onTable;
                      const cc = getChipColor(chip.denomination);
                      return (
                        <TableRow key={`g-${chip.denomination}`} sx={{ backgroundColor: cc.bg + "18" }}>
                          <TableCell><ChipToken denomination={chip.denomination} size={40} /></TableCell>
                          <TableCell align="center" sx={{ width: 120 }}>
                            <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                              <Typography variant="caption" color={onTable === 0 ? "error" : "text.secondary"}>
                                {onTable}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">/</Typography>
                              <TextField
                                size="small" type="number"
                                value={qty}
                                onChange={(e) => setGiven({ ...given, [chip.denomination]: e.target.value })}
                                inputProps={{ min: 0, max: onTable }}
                                disabled={onTable === 0}
                                error={isOver}
                                helperText={isOver ? `Max ${onTable}` : ""}
                                sx={{
                                  width: 70,
                                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                                  "& input[type=number]": { MozAppearance: "textfield" },
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={amount > 0 ? "error.main" : "text.disabled"} fontWeight={600}>
                              {amount > 0 ? `${amount.toLocaleString()}` : "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* CHIPS RECEIVED (player takes back) */}
            <Grid item xs={6}>
              <Typography fontWeight={700} mb={1} color="success.main">Chips Received (Player Gets)</Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f3fff6" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Denom ()</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Stock / Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockChips.map((chip) => {
                      const qty      = received[chip.denomination] || "";
                      const amount   = calcAmount(chip.denomination, qty);
                      const isOver   = Number(qty) > chip.quantity;
                      const cc = getChipColor(chip.denomination);
                      return (
                        <TableRow key={`r-${chip.denomination}`} sx={{ backgroundColor: cc.bg + "18" }}>
                          <TableCell><ChipToken denomination={chip.denomination} size={40} /></TableCell>
                          <TableCell align="center" sx={{ width: 120 }}>
                            <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                              <Typography variant="caption" color={chip.quantity === 0 ? "error" : "text.secondary"}>
                                {chip.quantity}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">/</Typography>
                              <TextField
                                size="small" type="number"
                                value={qty}
                                onChange={(e) => setReceived({ ...received, [chip.denomination]: e.target.value })}
                                inputProps={{ min: 0 }}
                                error={isOver}
                                helperText={isOver ? `Max ${chip.quantity}` : ""}
                                sx={{
                                  width: 70,
                                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                                  "& input[type=number]": { MozAppearance: "textfield" },
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={amount > 0 ? "success.main" : "text.disabled"} fontWeight={600}>
                              {amount > 0 ? `${amount.toLocaleString()}` : "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* BALANCE SUMMARY */}
        <Paper
          sx={{
            p: 2, mb: 3, borderRadius: 2,
            background: isBalanced
              ? "linear-gradient(135deg,#11998e,#38ef7d)"
              : difference > 0
              ? "linear-gradient(135deg,#b71c1c,#e53935)"
              : difference < 0
              ? "linear-gradient(135deg,#e65100,#f59e0b)"
              : "#f5f5f5",
            color: totalGiven > 0 || totalReceived > 0 ? "#fff" : "inherit",
          }}
        >
          <Grid container alignItems="center">
            <Grid item xs={4} textAlign="center">
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Given</Typography>
              <Typography variant="h6" fontWeight={700}>{totalGiven.toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <SwapHorizIcon sx={{ fontSize: 32, opacity: 0.8 }} />
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Received</Typography>
              <Typography variant="h6" fontWeight={700}>{totalReceived.toLocaleString()}</Typography>
            </Grid>
          </Grid>
          {!isBalanced && totalGiven > 0 && (
            <Typography variant="caption" display="block" textAlign="center" mt={0.5}>
              {difference > 0
                ? `⚠ Received exceeds given by ${Math.abs(difference).toLocaleString()}`
                : `⚠ Given exceeds received by ${Math.abs(difference).toLocaleString()}`}
            </Typography>
          )}
          {isBalanced && (
            <Typography variant="caption" display="block" textAlign="center" mt={0.5}>
              ✓ Exchange is balanced
            </Typography>
          )}
        </Paper>

        {/* OTHER FIELDS */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} size="small" label="Remarks (optional)"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Grid>

          {submitError && (
            <Grid item xs={12}>
              <Alert severity="error">{submitError}</Alert>
            </Grid>
          )}

          {submitWarnings.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ fontWeight: 600 }}>
                <strong>Denomination Overflow Warning</strong>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                  {submitWarnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={<SwapHorizIcon />}
                onClick={handleSave}
                disabled={loading || !isBalanced || !form.date}
              >
                {loading ? "Processing..." : "Exchange Chips"}
              </Button>
            </Box>
          </Grid>
        </Grid>

      </Box>
    </Drawer>
  );
}
